import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, ListChecks, StickyNote, Trash2 } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, newIdempotencyKey } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { BatchSearch, StoreSelect, formatQty, invalidateStockControl, parseQty, productTitle, storeLabel, useStoreOptions } from './stock-control-common';
import type { StockBatchHit } from './stock-control-types';

const APPROVALS_TAB = '/stock-control?tab=approvals';

interface TransferLine {
  batch: StockBatchHit;
  qty: string;
}

interface TransferResult {
  transferred: number;
}

function quantityFor(line: TransferLine): number | null {
  const qty = parseQty(line.qty);
  if (qty === null || qty <= 0 || qty > line.batch.qtyOnHand) return null;
  return qty;
}

export function TransferStockPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), APPROVALS_TAB);
  const stores = useStoreOptions();
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [fromStoreId, setFromStoreId] = useState('');
  const [toStoreId, setToStoreId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const storeName = (id: string) => {
    const store = stores.data?.find((candidate) => candidate.id === id);
    return store ? storeLabel(store) : 'the other store';
  };

  const addLine = (batch: StockBatchHit) => {
    setLines((current) => (current.some((line) => line.batch.id === batch.id) ? current : [...current, { batch, qty: '' }]));
  };

  const submit = useMutation({
    mutationFn: () =>
      post<TransferResult>(
        '/pharmacy/stock/transfer',
        {
          fromStoreId,
          toStoreId,
          lines: lines.map((line) => ({ stockBatchId: line.batch.id, qty: quantityFor(line) })),
          notes: notes.trim() || undefined,
        },
        { idempotencyKey },
      ),
    onSuccess: (result) => {
      toast.success(
        `Transferred ${result.transferred} batch${result.transferred === 1 ? '' : 'es'}`,
        tr('pharmacy', 'from_x_to_x', { fromStoreId: storeName(fromStoreId), toStoreId: storeName(toStoreId) }),
      );
      void invalidateStockControl(queryClient);
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (!fromStoreId) missing.push('the store it leaves');
  if (!toStoreId) missing.push('the store it goes to');
  if (lines.length === 0) missing.push('at least one batch');
  if (lines.some((line) => quantityFor(line) === null)) missing.push('a quantity above zero, no more than on hand, on every line');

  const dirty = !submit.isSuccess && (fromStoreId !== '' || toStoreId !== '' || notes !== '' || lines.length > 0);

  return (
    <FormScreen
      title={tr('pharmacy', 'transfer_stock')}
      description={tr('pharmacy', 'move_batches_from_one_store_to_another')}
      eyebrow={tr('pharmacy', 'stock_control')}
      icon={ArrowRightLeft}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (missing.length > 0) return;
        setError(null);
        submit.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={ArrowRightLeft}>
        <Field label={tr('pharmacy', 'from_store')} required hint={lines.length > 0 ? tr('pharmacy', 'remove_every_line_to_change_the_store') : undefined}>
          <StoreSelect
            value={fromStoreId}
            onChange={(storeId) => {
              setFromStoreId(storeId);
              if (storeId === toStoreId) setToStoreId('');
            }}
            stores={stores.data}
            emptyLabel={tr('pharmacy', 'select')}
            disabled={lines.length > 0}
          />
        </Field>
        <Field label={tr('pharmacy', 'to_store')} required>
          <StoreSelect value={toStoreId} onChange={setToStoreId} stores={stores.data} emptyLabel={tr('pharmacy', 'select')} excludeId={fromStoreId} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'batches')} icon={ListChecks} columns={1}>
        <BatchSearch
          storeId={fromStoreId}
          status="active"
          disabled={!fromStoreId}
          disabledHint="Choose the store the stock leaves to search its batches."
          isPicked={(batch) => lines.some((line) => line.batch.id === batch.id)}
          onPick={addLine}
        />

        {lines.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{tr('pharmacy', 'no_batches_added_yet_search_above_and_choose_2')}</p>
        ) : (
          <div className="space-y-2">
            {lines.map((line, index) => (
              <div key={line.batch.id} className="grid items-center gap-2 rounded-md border p-2 sm:grid-cols-12">
                <div className="sm:col-span-7">
                  <p className="text-sm font-medium">{productTitle(line.batch.product)}</p>
                  <p className="text-xs text-muted-foreground">
                    {tr('pharmacy', 'batch')} <span className="font-mono">{line.batch.batchNo}</span> {tr('pharmacy', 'expires_2')} {dateOnly(line.batch.expiryDate)} ·{' '}
                    {formatQty(line.batch.qtyOnHand)} {line.batch.product.unit} {tr('pharmacy', 'on_hand_3')}
                  </p>
                </div>
                <Input
                  className="tabular sm:col-span-4"
                  type="number"
                  min={0}
                  max={line.batch.qtyOnHand}
                  step="any"
                  aria-label={tr('pharmacy', 'quantity_to_move_for_x', { batchNo: line.batch.batchNo })}
                  placeholder={tr('pharmacy', 'qty_max_x', { qtyOnHand: formatQty(line.batch.qtyOnHand) })}
                  value={line.qty}
                  onChange={(event) =>
                    setLines((current) => current.map((candidate, position) => (position === index ? { ...candidate, qty: event.target.value } : candidate)))
                  }
                />
                <Button
                  type="button"
                  className="sm:col-span-1"
                  variant="ghost"
                  size="icon"
                  aria-label={tr('pharmacy', 'remove_x', { batchNo: line.batch.batchNo })}
                  onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </FormSection>

      <FormSection title={tr('pharmacy', 'notes')} icon={StickyNote} columns={1}>
        <Field label={tr('pharmacy', 'notes')}>
          <Textarea rows={2} maxLength={600} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={tr('pharmacy', 'transfer')}
        loading={submit.isPending}
        incomplete={missing.length > 0}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('pharmacy', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
